import {typeboxResolver} from '@hookform/resolvers/typebox'
import {announce} from '@react-aria/live-announcer'
import {type Static, Type} from '@sinclair/typebox'
import {TypeCompiler} from '@sinclair/typebox/compiler'
import {Value} from '@sinclair/typebox/value'
import {Button, FieldError, Form, Input, Label, TextField} from 'react-aria-components'
import {Controller, type SubmitHandler, useForm} from 'react-hook-form'
import {toast} from 'sonner'

import googleSvg from '@/assets/icons/social/google.svg'
import decorationSvg from '@/assets/images/decoration.svg'
import LogoSvg from '@/assets/images/logo.svg?react'
import {LOGIN_DEFAULT_REDIRECT} from '@/auth/config'
import PasswordInput from '@/components/PasswordInput/PasswordInput'
import SkipLink from '@/components/SkipLink'
import {PRIVACY_POLICY, TERMS_OF_USE} from '@/constants/contacts'
import Navbar from '@/layout/Navbar'
import HeadTags from '@/lib/head/HeadTags'
import isInApp from '@/lib/in-app/utils/isInApp'
import {useLocale} from '@/lib/locale/locale'
import getReferraId from '@/lib/referral/getReferralId'
import {HomeRoute, SignInRoute, SignUpRoute} from '@/routeRegistry'
import {signIn, signUp} from '@/services/authenticationService'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import useClientValue from '@/utils/hooks/useClientValue'
import postMessageToReactNative from '@/utils/postMessageToReactNative'
import waitAtLeast from '@/utils/waitAtLeast'

import style from './SignUp.module.scss'

const schema = Type.Object({
  email: Type.String({
    default: '',
    format: 'email',
    errorMessage: 'Please enter a valid email address',
  }),
  fullName: Type.String({
    minLength: 1,
    default: '',
    errorMessage: "Your name shouldn't be left empty",
  }),
  password: Type.String({
    default: '',
    minLength: 8,
    format: 'password',
    errorMessage:
      'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter & one number',
  }),
  referrerId: Type.String({
    default: getReferraId() ?? '',
  }),
  phone: Type.String({
    default: '-1',
  }),
})

const typecheck = TypeCompiler.Compile(schema)

type Schema = Static<typeof schema>

export default memo(function SignUp() {
  const {redirect} = SignUpRoute.useSearch()
  const referrerId = getReferraId()
  const [locale] = useLocale()

  const redirectTo = redirect ?? LOGIN_DEFAULT_REDIRECT
  const redirectFull = useClientValue(() => new URL(redirectTo, window.location.origin))

  const [isSigningUp, setIsSigningUp] = useState(false)

  const {handleSubmit, control, setError} = useForm<Schema>({
    mode: 'onTouched',
    resolver: typeboxResolver(typecheck),
    defaultValues: Value.Create(schema),
  })

  const onSubmit: SubmitHandler<Schema> = data => {
    setIsSigningUp(true)
    void signUp({
      email: data.email,
      fullName: data.fullName,
      phone: data.phone,
      password: data.password,
      referrerId: data.referrerId,
    })
      .then(() => {
        const successMessage = 'Sign up successfully! Signing you in....'
        announce(successMessage)
        toast.promise(
          waitAtLeast(signIn({email: data.email, password: data.password, locale}), 1000),
          {
            loading: successMessage,
            success: () => {
              const loginSuccessMessage =
                'You have successfully signed up and signed in. You can now access your account.'
              announce(loginSuccessMessage)
              return loginSuccessMessage
            },
            error: (error: Error) => {
              announce(error.message)
              return error.message
            },
          },
        )
      })
      .catch((error: Error) => {
        announce(error.message)
        setError(
          'email',
          {message: error.message},
          {
            shouldFocus: true,
          },
        )
      })
      .finally(() => {
        setIsSigningUp(false)
      })
  }

  const handleGoogleSignIn = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (isInApp()) {
      postMessageToReactNative({
        type: 'google-login',
        redirect: redirectTo,
        referrerId,
      })
    } else {
      window.location.href = `/api/auth/oauth2/authorization/google?origUrl=${
        redirectFull?.href ?? ''
      }${referrerId ? `&referrerId=${referrerId}` : ''}`
    }
  }

  return (
    <>
      <HeadTags title='Sign up' />
      <SkipLink title='Skip to main content' to='#main-content' />
      <div className={style.SignUpPage}>
        <img src={decorationSvg} className={style.Decoration} alt='' />
        <div className={style.Content}>
          <Navbar withoutLogoOnDesktop transparentBg variant='dark' />
          <div className={style.Logo}>
            <Link to={HomeRoute.fullPath}>
              <LogoSvg title='InnoSearch Home' />
            </Link>
          </div>
          <main className={clsx(style.Main)} {...skipTargetProps('main-content')}>
            <h1 className={clsx(style.Heading)}>Sign up now</h1>
            <Form onSubmit={handleSubmit(onSubmit)} className={style.Form}>
              <Controller
                control={control}
                name='fullName'
                render={({
                  field: {name, value, onChange, onBlur, ref},
                  fieldState: {invalid, error},
                }) => (
                  <TextField
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    validationBehavior='aria'
                    isInvalid={invalid}
                    isRequired
                    className={style.Field}
                  >
                    <Label className={style.Label}>Full name</Label>
                    <Input className={style.Input} ref={ref} />
                    {!!error?.message && (
                      <FieldError className={style.Error}>{error.message}</FieldError>
                    )}
                  </TextField>
                )}
              />
              <Controller
                control={control}
                name='email'
                render={({
                  field: {name, value, onChange, onBlur, ref},
                  fieldState: {invalid, error},
                }) => (
                  <TextField
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    validationBehavior='aria'
                    isInvalid={invalid}
                    isRequired
                    className={style.Field}
                  >
                    <Label className={style.Label}>Email</Label>
                    <Input className={style.Input} ref={ref} />
                    {!!error?.message && (
                      <FieldError className={style.Error}>{error.message}</FieldError>
                    )}
                  </TextField>
                )}
              />
              <Controller
                control={control}
                name='password'
                render={({
                  field: {name, value, onChange, onBlur, ref},
                  fieldState: {invalid, error},
                }) => (
                  <TextField
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    validationBehavior='aria'
                    isInvalid={invalid}
                    isRequired
                    className={style.Field}
                    type='password'
                  >
                    <Label className={style.Label}>Password</Label>
                    <PasswordInput ref={ref} className={clsx(style.Input, style.InputGroup)} />
                    {!!error?.message && (
                      <FieldError className={style.Error}>{error.message}</FieldError>
                    )}
                  </TextField>
                )}
              />
              <Controller
                control={control}
                name='referrerId'
                render={({
                  field: {name, value, onChange, onBlur, ref},
                  fieldState: {invalid, error},
                }) => (
                  <TextField
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    validationBehavior='aria'
                    isInvalid={invalid}
                    className={style.Field}
                  >
                    <Label className={style.Label}>
                      (Optional) Referred by a friend? Enter their code here
                    </Label>
                    <Input className={style.Input} ref={ref} />
                    {!!error?.message && (
                      <FieldError className={style.Error}>{error.message}</FieldError>
                    )}
                  </TextField>
                )}
              />
              <div className={style.Agreement}>
                By creating an account, I agree to our{' '}
                <a href={TERMS_OF_USE} target='_blank' rel='noreferrer'>
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href={PRIVACY_POLICY} target='_blank' rel='noreferrer'>
                  Privacy Policy
                </a>
              </div>
              <div className={style.SubmitArea}>
                <Button className={style.SignUpBtn} type='submit' isDisabled={isSigningUp}>
                  {!isSigningUp ? 'Sign up' : 'Signing up...'}
                </Button>
                <div className={style.Login}>
                  <span>
                    Already have an account?{' '}
                    <Link
                      to={SignInRoute.fullPath}
                      search={{
                        redirect,
                      }}
                    >
                      Sign in
                    </Link>
                  </span>
                </div>
              </div>
              {!isInApp() && (
                <div className={style.OAuth}>
                  {/* <a
                    href={
                      '/api/auth/oauth2/authorization/facebook?origUrl=' + redirectToLocation.href
                    }
                    className={style.OAuthItem}
                    aria-label='Sign in with Facebook'
                  >
                    <img src={facebook2Svg} alt='' />
                  </a> */}
                  {/* <div className={style.OAuthItem}>Apple</div> */}
                  <button
                    onClick={handleGoogleSignIn}
                    className={style.OAuthItem}
                    aria-label='Sign in with Google'
                  >
                    <img src={googleSvg} alt='' />
                  </button>
                  {/* <div className={style.OAuthItem}>Twitter</div> */}
                </div>
              )}
            </Form>
          </main>
        </div>
      </div>
    </>
  )
})
